/**
 * MergedStageAssociationEditor — editable stage picker for one pipeline/lifecycle
 * stage association on a merged create-action card.
 *
 * Stage targets aren't searchable entities (you don't free-text search for a
 * stage — you pick from the entity's pipeline), so they get the canonical stage
 * dropdown (AssociatedPipelineStageInput) instead of the entity typeahead used
 * by AssociationTargetEditor. Mirrors the standalone StageAssociationCard's edit
 * path, but reports the selected stage id up to MergedCreateEditForm so the whole
 * card commits under one combined save.
 *
 * Rendered per stage association (a component, not an inline map) so the stage
 * hooks run once per row rather than in a loop.
 */

import { useEffect } from 'react';

import { AssociatedPipelineStageInput } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';

import type { AssociationAddedEntityPayload } from '../../lib/proposals/core/types';
import { isAssociationAddedPayload } from '../../lib/proposals/core/types';
import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import { useStageEditState } from './shared';

interface MergedStageAssociationEditorProps {
  assoc: MergedAssociation;
  orgId: number;
  /** Reports the currently-selected stage entity_id up to the parent form. */
  onChange: (actionId: number, stageId: string | null) => void;
}

export function MergedStageAssociationEditor({
  assoc,
  orgId,
  onChange,
}: MergedStageAssociationEditorProps) {
  // The association-added payload's entity_type_name is the SOURCE entity type
  // (e.g. ahoy_deal / ahoy_contact) — used to scope the stage dropdown to the
  // right pipeline(s). Guarded so a malformed payload renders nothing.
  const payload = isAssociationAddedPayload(assoc.action.payload)
    ? (assoc.action.payload as AssociationAddedEntityPayload)
    : null;
  const entityType = payload?.entity_type_name ?? '';

  const { editStageId, handleStageChange } = useStageEditState({
    proposedValue: assoc.targetEntityId,
    orgId,
    entityType,
  });

  // Mirror the selected stage id up to the parent (for dirty tracking + the
  // combined save). onChange is a stable callback from MergedCreateEditForm.
  // Reported under the association's version-stable identity, matching the key
  // the parent's `editedStageIds` map uses — a partial save can re-version this
  // association while the form is still open, and a raw row id would orphan
  // the entry.
  const actionKey = getStableActionKey(assoc.action);
  useEffect(() => {
    onChange(actionKey, editStageId);
  }, [actionKey, editStageId, onChange]);

  if (!payload) {
    return null;
  }

  return (
    <div className='flex flex-col gap-1.5'>
      <label className='text-xs font-medium text-fg-tertiary'>Stage</label>
      <AssociatedPipelineStageInput
        value={editStageId}
        onChange={handleStageChange}
        entityType={entityType}
        associationTypeName={assoc.associationType}
        disableAutoSelect
        ariaLabel='Stage'
      />
    </div>
  );
}
